import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { UserProvider, useUser } from './contexts/UserContext';
import { LOCAL_AUTH_TOKEN_KEY } from './constants/auth';
import Layout from './components/Layout';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Upload from './pages/Upload';
import Reports from './pages/Reports';
import Users from './pages/Users';
import Agencies from './pages/Agencies';
import Groups from './pages/Groups';
import SystemParameters from './pages/SystemParameters';
import Audit from './pages/Audit';
import UserGuide from './pages/UserGuide';
import EmailOtpGate from './components/EmailOtpGate';

// Protected Route: MSAL and/or local JWT; user must exist in TAP with roles
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isMsalAuthenticated = useIsAuthenticated();
  const { user, loading, error, emailOtpPending } = useUser();
  const hasLocalToken =
    typeof window !== 'undefined' && !!localStorage.getItem(LOCAL_AUTH_TOKEN_KEY);

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          fontFamily: 'var(--font-family)',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '16px' }}>Loading...</div>
          <div style={{ color: '#6c757d' }}>Validating user access...</div>
        </div>
      </div>
    );
  }

  if (emailOtpPending) {
    return <EmailOtpGate />;
  }

  if (!hasLocalToken && !isMsalAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (
    error ||
    !user ||
    !user.roles ||
    user.roles.length === 0 ||
    user.isActive === false
  ) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function NotCustomerRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useUser();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        fontFamily: 'var(--font-family)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '16px' }}>Loading...</div>
          <div style={{ color: '#6c757d' }}>Validating access...</div>
        </div>
      </div>
    );
  }

  const cannotAccessUploadOrReports = !!user?.roles?.some(
    (role: any) =>
      role.id === 'customer-role-id' || role.id === 'customer-admin-role-id',
  );
  if (cannotAccessUploadOrReports) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function LoginRoute() {
  const { user, loading, emailOtpPending } = useUser();
  const isMsalAuthenticated = useIsAuthenticated();

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          fontFamily: 'var(--font-family)',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem' }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (emailOtpPending && isMsalAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (
    user &&
    user.roles &&
    user.roles.length > 0 &&
    user.isActive !== false
  ) {
    return <Navigate to="/" replace />;
  }

  return <Login />;
}

/** Public pages (forgot/reset): redirect authenticated users to home. */
function GuestOnlyRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useUser();

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          fontFamily: 'var(--font-family)',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem' }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (user && user.roles && user.roles.length > 0 && user.isActive !== false) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  const isAuthenticated = useIsAuthenticated();
  const { accounts, instance } = useMsal();
  
  // Debug logging
  console.log('App - isAuthenticated:', isAuthenticated);
  console.log('App - accounts:', accounts);
  console.log('App - current URL:', window.location.href);
  console.log('App - current pathname:', window.location.pathname);
  console.log('App - hash:', window.location.hash);
  console.log('App - search:', window.location.search);
  
  // IMPORTANT: MsalProvider automatically calls handleRedirectPromise() on mount
  // We should NOT call it manually here to avoid race conditions.
  // However, we can monitor the redirect processing and provide debugging info.
  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    const hasAuthResponse = hash.includes('code=') || search.includes('code=') || hash.includes('error=');
    
    if (hasAuthResponse) {
      console.log('[App] ⚠️ Auth response detected in URL');
      console.log('[App] Hash present:', hash.substring(0, 100) + '...');
      console.log('[App] Configured redirect URI:', instance.getConfiguration().auth.redirectUri);
      console.log('[App] Current URL:', window.location.origin + window.location.pathname);
      console.log('[App] Full URL matches redirect URI?', 
        (window.location.origin + window.location.pathname) === instance.getConfiguration().auth.redirectUri?.replace(/\/$/, ''));
      
      // Extract state from URL to debug
      try {
        const hashParams = new URLSearchParams(hash.substring(1));
        const stateFromUrl = hashParams.get('state');
        const codeFromUrl = hashParams.get('code');
        const errorFromUrl = hashParams.get('error');
        
        console.log('[App] Auth response details:', {
          hasCode: !!codeFromUrl,
          hasState: !!stateFromUrl,
          hasError: !!errorFromUrl,
          error: errorFromUrl,
          statePreview: stateFromUrl ? decodeURIComponent(stateFromUrl).substring(0, 50) + '...' : 'none'
        });
        
        // Check MSAL cache state
        const allAccounts = instance.getAllAccounts();
        console.log('[App] Accounts in cache:', allAccounts.length);
        
        // Check localStorage for MSAL state keys
        try {
          const msalCacheKeys = Object.keys(localStorage).filter(key => key.startsWith('msal.'));
          console.log('[App] MSAL cache keys:', msalCacheKeys.length);
          if (msalCacheKeys.length > 0 && process.env.NODE_ENV === 'development') {
            console.log('[App] MSAL cache keys (dev only):', msalCacheKeys);
          }
        } catch (e) {
          console.warn('[App] Could not check localStorage:', e);
        }
        
        // If there's an error in the URL, log it
        if (errorFromUrl) {
          const errorDescription = hashParams.get('error_description');
          console.error('[App] ❌ Auth error in redirect:', errorFromUrl, errorDescription);
        }
      } catch (e) {
        console.warn('[App] Could not parse hash params:', e);
      }
    }
  }, [instance]);
  
  // Clean up URL ONLY after MSAL has successfully processed the redirect and user is authenticated
  // This ensures MSAL has time to validate the state and process the auth code
  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    const hasRedirectParams = hash.includes('code=') || search.includes('code=') || hash.includes('error=');
    
    // Only clean up if:
    // 1. There are redirect params in the URL
    // 2. User is now authenticated (MSAL successfully processed the redirect)
    // 3. We have at least one account (confirming authentication succeeded)
    if (hasRedirectParams && isAuthenticated && accounts.length > 0) {
      console.log('[App] ✅ Authentication successful, cleaning up URL');
      
      // Small delay to ensure MSAL has fully processed the redirect
      const cleanupTimer = setTimeout(() => {
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, '', cleanUrl);
        console.log('[App] URL cleaned:', cleanUrl);
      }, 100);
      
      return () => clearTimeout(cleanupTimer);
    }
    
    // Handle callback route separately
    if (window.location.pathname === '/auth/callback' && isAuthenticated && accounts.length > 0) {
      console.log('[App] Authentication complete at callback route, redirecting to home...');
      window.location.href = '/';
    }
  }, [isAuthenticated, accounts.length]);

  return (
    <UserProvider>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route
            path="/forgot-password"
            element={
              <GuestOnlyRoute>
                <ForgotPassword />
              </GuestOnlyRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <GuestOnlyRoute>
                <ResetPassword />
              </GuestOnlyRoute>
            }
          />
          <Route path="/auth/callback" element={<div>Processing authentication...</div>} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route
              path="upload"
              element={
                <NotCustomerRoute>
                  <Upload />
                </NotCustomerRoute>
              }
            />
            <Route
              path="reports"
              element={
                <NotCustomerRoute>
                  <Reports />
                </NotCustomerRoute>
              }
            />
            <Route path="user-guide" element={<UserGuide />} />
            <Route path="users" element={<Users />} />
            <Route path="agencies" element={<Agencies />} />
            <Route path="groups" element={<Groups />} />
            <Route path="system-parameters" element={<SystemParameters />} />
            <Route path="audit" element={<Audit />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </UserProvider>
  );
}

export default App;


